import { useEffect, useRef, useState, useCallback } from 'react';

const BASE_RECONNECT_DELAY = 1000;  // 1s, doubles each attempt
const MAX_RECONNECT_DELAY = 8000;   // cap at 8s
const MAX_SILENT_RETRIES = 3;       // try 3 times silently before going quiet
const PING_INTERVAL = 30000;        // 30s heartbeat

/**
 * Hook para conexão WebSocket em tempo real.
 *
 * Reconexão silenciosa em segundo plano: até MAX_SILENT_RETRIES tentativas
 * com backoff exponencial (1s → 2s → 4s). Após esgotar, o hook fica em
 * standby sem derrubar a sessão nem exibir erro. Uma nova chamada a
 * connect() (ex.: troca de id) zera o contador.
 */
export const useWebSocket = (type, id, onMessage) => {
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const retryCountRef = useRef(0);

  const connect = useCallback(() => {
    if (!id) return;

    // URL do WebSocket
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = process.env.REACT_APP_BACKEND_URL?.replace(/^https?:\/\//, '') || window.location.host;
    const wsUrl = `${protocol}//${host}/api/ws/${type}/${id}`;

    console.log(`[WebSocket] Connecting to ${wsUrl} (attempt ${retryCountRef.current + 1}/${MAX_SILENT_RETRIES + 1})`);

    try {
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log(`[WebSocket] Connected as ${type}: ${id}`);
        setIsConnected(true);
        retryCountRef.current = 0; // Conexão saudável → zera o contador

        // Inicia ping interval para manter conexão viva
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, PING_INTERVAL);
      };

      ws.onmessage = (event) => {
        // Ignora respostas de pong
        if (event.data === 'pong') return;

        try {
          const message = JSON.parse(event.data);
          console.log(`[WebSocket] Message received:`, message);
          onMessage?.(message);
        } catch (e) {
          console.error('[WebSocket] Failed to parse message:', e);
        }
      };

      ws.onclose = (event) => {
        console.log(`[WebSocket] Disconnected. Code: ${event.code}, Reason: ${event.reason}`);
        setIsConnected(false);

        // Limpa ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }

        // Fechamento intencional (1000) → não tenta reconectar
        if (event.code === 1000) return;

        // Reconexão silenciosa com backoff exponencial, até MAX_SILENT_RETRIES.
        // Depois disso, fica em standby sem incomodar o aluno.
        if (retryCountRef.current >= MAX_SILENT_RETRIES) {
          console.warn('[WebSocket] Max silent retries reached. Session kept alive, real-time updates paused.');
          return;
        }

        const delay = Math.min(
          BASE_RECONNECT_DELAY * Math.pow(2, retryCountRef.current),
          MAX_RECONNECT_DELAY
        );
        retryCountRef.current += 1;

        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log(`[WebSocket] Silent reconnect attempt ${retryCountRef.current}/${MAX_SILENT_RETRIES}...`);
          connect();
        }, delay);
      };

      ws.onerror = (error) => {
        // Erros aqui geralmente disparam o onclose logo em seguida; não fazemos
        // toast nem alerta para o usuário — esse é o protocolo de "silent reconnection".
        console.error('[WebSocket] Error:', error);
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('[WebSocket] Connection error:', error);
      if (retryCountRef.current < MAX_SILENT_RETRIES) {
        const delay = Math.min(
          BASE_RECONNECT_DELAY * Math.pow(2, retryCountRef.current),
          MAX_RECONNECT_DELAY
        );
        retryCountRef.current += 1;
        reconnectTimeoutRef.current = setTimeout(connect, delay);
      }
    }
  }, [type, id, onMessage]);

  const disconnect = useCallback(() => {
    // Limpa timers
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    // Fecha conexão com código 1000 (normal closure) para não reagendar
    if (wsRef.current) {
      try { wsRef.current.close(1000, 'client disconnect'); } catch (_e) { /* ignore */ }
      wsRef.current = null;
    }

    setIsConnected(false);
  }, []);

  useEffect(() => {
    retryCountRef.current = 0; // novo id → nova "sessão" de retries
    connect();

    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  return { isConnected, disconnect };
};

export default useWebSocket;
